import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Plus, Search, Building2, ChevronRight } from 'lucide-react';
import { clsx } from 'clsx';
import { adminApi } from '@/lib/api';

const STATUS_STYLES: Record<string, string> = {
  active:       'bg-clinical-success/10 text-clinical-success',
  pending:      'bg-orange/10 text-orange-dark',
  under_review: 'bg-blue-50 text-clinical-info',
  suspended:    'bg-red-50 text-clinical-danger',
  cancelled:    'bg-gray-100 text-gray-400',
  retired:      'bg-gray-100 text-gray-400',
};

const STATUS_LABELS: Record<string, string> = {
  active:       'Active',
  pending:      'Pending',
  under_review: 'Under Review',
  suspended:    'Suspended',
  cancelled:    'Cancelled',
  retired:      'Retired',
};

const PLAN_BADGES: Record<string, string> = {
  base: 'bg-gray-100 text-gray-600',
  plus: 'bg-blue-50 text-clinical-info',
  pro:  'bg-purple-50 text-purple-600',
};

const PLAN_MRR: Record<string, string> = {
  base: '₹1,999',
  plus: '₹3,999',
  pro:  '₹6,999',
};

export default function HospitalsList() {
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage]     = useState(1);

  const { data, isLoading } = useQuery({
    queryKey: ['admin-hospitals', search, status, page],
    queryFn:  () => adminApi.hospitals({ search, status, page, limit: 20 }).then(r => r.data),
    staleTime: 10_000,
  });

  const hospitals: any[] = data?.rows  ?? [];
  const total: number    = data?.total ?? 0;

  return (
    <div className="p-8 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-navy">Hospitals</h1>
          <p className="text-gray-400 text-sm mt-0.5">
            {total} hospital{total !== 1 ? 's' : ''} registered
          </p>
        </div>
        <Link to="/admin/hospitals/new"
          className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-orange text-navy
                     font-semibold text-sm hover:bg-orange-dark transition-colors">
          <Plus size={16}/> New Hospital
        </Link>
      </div>

      {/* Filters */}
      <div className="flex gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"/>
          <input
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search hospitals…"
            className="w-full pl-9 pr-4 py-2 rounded-lg border border-gray-200 text-sm
                       focus:outline-none focus:ring-2 focus:ring-orange/30 focus:border-orange"
          />
        </div>
        <select
          value={status}
          onChange={e => { setStatus(e.target.value); setPage(1); }}
          className="px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none
                     focus:ring-2 focus:ring-orange/30 focus:border-orange text-navy">
          <option value="">All statuses</option>
          {Object.entries(STATUS_LABELS).map(([v, l]) => (
            <option key={v} value={v}>{l}</option>
          ))}
        </select>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-gray-400 text-sm">Loading…</div>
        ) : hospitals.length === 0 ? (
          <div className="p-12 flex flex-col items-center gap-3 text-gray-400">
            <Building2 size={32} className="text-gray-200"/>
            <p className="text-sm">No hospitals found</p>
            <Link to="/admin/hospitals/new"
              className="text-orange text-sm hover:underline font-medium">
              Register the first hospital →
            </Link>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {['Hospital', 'Location', 'Plan', 'Users', 'MRR', 'Status', ''].map(h => (
                  <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {hospitals.map((h: any) => (
                <tr key={h.id} className="hover:bg-gray-50 transition-colors group">
                  <td className="px-4 py-3">
                    <div className="font-medium text-navy">{h.hospital_name || '—'}</div>
                    <div className="text-xs text-gray-400 font-mono">{h.schema_name}</div>
                  </td>
                  <td className="px-4 py-3 text-gray-500 text-sm">
                    {h.city}{h.state ? `, ${h.state}` : ''}
                  </td>
                  <td className="px-4 py-3">
                    {h.plan ? (
                      <span className={clsx(
                        'inline-flex px-2 py-0.5 rounded-full text-xs font-medium uppercase',
                        PLAN_BADGES[h.plan] ?? 'bg-gray-100 text-gray-500'
                      )}>
                        {h.plan}
                      </span>
                    ) : <span className="text-gray-300 text-xs">—</span>}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    <span className="font-medium">{h.user_count ?? 0}</span>
                    <span className="text-gray-300 mx-1">/</span>
                    <span className="text-gray-400">{h.max_users ?? '?'}</span>
                  </td>
                  <td className="px-4 py-3 text-sm font-medium text-navy">
                    {h.plan ? PLAN_MRR[h.plan] ?? '—' : '—'}
                  </td>
                  <td className="px-4 py-3">
                    <span className={clsx(
                      'inline-flex px-2 py-0.5 rounded-full text-xs font-medium',
                      STATUS_STYLES[h.status] ?? 'bg-gray-100 text-gray-400'
                    )}>
                      {STATUS_LABELS[h.status] ?? h.status}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <Link
                      to={`/admin/hospitals/${h.id}`}
                      className="flex items-center gap-1 text-xs text-gray-400 hover:text-orange
                                 opacity-0 group-hover:opacity-100 transition-all font-medium">
                      View <ChevronRight size={12}/>
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {total > 20 && (
        <div className="flex items-center justify-between text-sm text-gray-500">
          <span>Showing {(page - 1) * 20 + 1}–{Math.min(page * 20, total)} of {total}</span>
          <div className="flex gap-2">
            <button onClick={() => setPage(p => p - 1)} disabled={page === 1}
              className="px-3 py-1.5 rounded-lg border border-gray-200 hover:bg-gray-50
                         disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
              Prev
            </button>
            <button onClick={() => setPage(p => p + 1)} disabled={page * 20 >= total}
              className="px-3 py-1.5 rounded-lg border border-gray-200 hover:bg-gray-50
                         disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
